import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import CheckboxTsoPq from '@components/Checkbox/CheckboxTsoPq';
import LoadingSpinnerTsoPq from '@components/LoadingSpinners/LoadingSpinnerTsoPq';
import { GridColDef } from '@mui/x-data-grid-pro';
import { customSortIconAsc, customSortIconDesc, StyledDataGridPC } from '@pages/Masterdata/StyledGridPageComponents';
import { useGetMeasurementParameterGetAllQuery } from '@redux/apiSlices/measurementParameterApi';
import {
  ApiCommonExternalDtoPlausibilityCheckResultExternalDto,
  useGetPlausibilityCheckResultGetAllByImportOperationQuery,
  useLazyPutPlausibilityCheckResultUpdateStatusQuery,
} from '@redux/apiSlices/plausibilityCheckResultApi';
import { useAppDispatch } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { StyledButtonContainer, StyledCheckboxWrapper, StyledDataGridContainer } from '../StyledUploadDataComponents';

type TableRows = {
  id: number;
  start: string;
  end: string;
  plausibilityRule: string;
  plausibility: { isPlausible: boolean };
};

const PausibilityCheckDataGridTsoPq = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [gridRows, setGridRows] = useState<TableRows[]>([]);
  const [triggerPlausibilityCheckUpdate] = useLazyPutPlausibilityCheckResultUpdateStatusQuery();
  const { importoperationid: importOperationId } = useParams();
  const {
    data: plausibilityData,
    isLoading: plausibilityIsLoading,
    refetch: plausibilityDataRefetch,
  } = useGetPlausibilityCheckResultGetAllByImportOperationQuery(Number(importOperationId), {
    skip: !importOperationId,
  });

  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();

  const measurementParameterMap = useMemo(() => {
    const externalDtos = measurementParameters?.externalDtos;
    if (!externalDtos || externalDtos.length === 0) return {};

    return Object.fromEntries(externalDtos.map((param) => [param.id!, param.technicalName]));
  }, [measurementParameters]);

  const tableData = useMemo(() => {
    if (!plausibilityData?.externalDtos) return [];

    return plausibilityData?.externalDtos?.map((row: ApiCommonExternalDtoPlausibilityCheckResultExternalDto) => {
      const translatedRule = t(`${row.rule}`);
      const tehnicalName = measurementParameterMap[row.measurementParameterId];
      const ruleWithTechName = tehnicalName ? `${translatedRule} (${tehnicalName})` : translatedRule;

      return {
        id: row.id!,
        start: dayjs(row.start).format('YYYY-MM-DD HH:mm'),
        end: dayjs(row.end).format('YYYY-MM-DD HH:mm'),
        plausibilityRule: ruleWithTechName,
        plausibility: {
          isPlausible: row.isPlausible,
        },
      };
    });
  }, [plausibilityData, measurementParameterMap]);

  useEffect(() => {
    if (tableData.length > 0) {
      setGridRows(tableData);
    }
  }, [tableData]);

  const handleCheckChange = (id: string) => {
    setGridRows((prev) =>
      prev.map((row) =>
        row.id === Number(id) ? { ...row, plausibility: { isPlausible: !row.plausibility.isPlausible } } : row
      )
    );
  };

  const handleDiscard = () => {
    setGridRows(tableData);
  };

  const handleApply = async () => {
    const plausibilityChanged = tableData
      .map((tableRow) => {
        const foundGridRow = gridRows.find((gridRow) => gridRow.id === tableRow.id);
        if (foundGridRow && tableRow.plausibility.isPlausible !== foundGridRow.plausibility.isPlausible) {
          return {
            id: foundGridRow.id,
            isPlausible: foundGridRow.plausibility.isPlausible,
          };
        }
      })
      .filter((row) => row !== undefined) as { id: number; isPlausible: boolean }[];

    if (plausibilityChanged.length === 0) return;

    try {
      await Promise.all(
        plausibilityChanged.map(({ id, isPlausible }) =>
          triggerPlausibilityCheckUpdate({
            plausibilityCheckResultId: id,
            isPlausible,
          }).unwrap()
        )
      );

      await plausibilityDataRefetch();

      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.plausibilityChanges.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
    } catch (error: any) {
      console.error('Error applying plausibility changes:', error);
      const requestID = error?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.plausibilityChanges.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'start',
      headerName: t('upload.detailedView.plausibilityCheck.dataGrid.start'),
      width: 132,
      headerClassName: 'customBackgroundHeader',
      resizable: false,
    },
    {
      field: 'end',
      headerName: t('upload.detailedView.plausibilityCheck.dataGrid.end'),
      width: 132,
      headerClassName: 'customBackgroundHeader',
      resizable: false,
    },
    {
      field: 'plausibilityRule',
      headerName: t('upload.detailedView.plausibilityCheck.dataGrid.plausibilityRule'),
      minWidth: 724,
      flex: 1,
      headerClassName: 'customBackgroundHeader',
      resizable: false,
    },
    {
      field: 'plausibility',
      headerName: t('upload.detailedView.plausibilityCheck.dataGrid.plausibility'),
      width: 108,
      headerClassName: 'customBackgroundHeader',
      sortable: false,
      filterable: false,
      resizable: false,
      renderCell: (params: any) => (
        <StyledCheckboxWrapper>
          <CheckboxTsoPq
            checked={params.row.plausibility.isPlausible}
            onChange={(e) => {
              handleCheckChange(params.row.id);
            }}
          />
        </StyledCheckboxWrapper>
      ),
    },
  ];

  return (
    <>
      {plausibilityIsLoading ? (
        <LoadingSpinnerTsoPq />
      ) : (
        <StyledDataGridContainer>
          <StyledDataGridPC
            rows={gridRows}
            columns={columns}
            initialState={{
              sorting: {
                sortModel: [
                  { field: 'end', sort: 'desc' },
                  { field: 'start', sort: 'desc' },
                  { field: 'plausibilityRule', sort: 'asc' },
                ],
              },
            }}
            columnHeaderHeight={38}
            getRowHeight={() => 42}
            disableRowSelectionOnClick
            disableColumnReorder
            disableColumnPinning
            disableColumnMenu
            showCellVerticalBorder
            hideFooter
            localeText={{
              noRowsLabel: t('general.grid.noRows'),
              noResultsOverlayLabel: t('general.grid.noMatchingRows'),
            }}
            slots={{
              columnSortedAscendingIcon: customSortIconAsc,
              columnSortedDescendingIcon: customSortIconDesc,
            }}
            sx={{
              maxHeight: '458px', //set height of 10 rows + header in px
              overflowY: 'auto',
              '& .MuiDataGrid-scrollbar': {
                overflowX: 'hidden',
              },
              '& .MuiBadge-badge': {
                backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
                color: 'black',
                border: `1px solid ${tsoPqTheme.palette.stroke.color_neutral_stroke_str_ntrl_accent}`,
                borderRadius: '50%',
                fontSize: '0.5rem',
                lineHeight: '1.125rem',
                right: '0.5rem',
                width: 15,
                height: 15,
                minWidth: 0,
                paddingTop: '0.10rem',
                top: 13,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
              '& .MuiIconButton-root': {
                left: '1.25rem',
              },
            }}
          ></StyledDataGridPC>
          <StyledButtonContainer>
            <ButtonTsoPq
              onClick={() => handleDiscard()}
              buttonType={'outlinedPrimary'}
              textContent={t('upload.detailedView.plausibilityCheck.status.notPlausible.discardButton')}
            />
            <ButtonTsoPq
              onClick={handleApply}
              buttonType={'filledPrimary'}
              textContent={t('upload.detailedView.plausibilityCheck.status.notPlausible.applyButton')}
            />
          </StyledButtonContainer>
        </StyledDataGridContainer>
      )}
    </>
  );
};

export default PausibilityCheckDataGridTsoPq;
